import { useState } from "react";
import InputTemplate from "shared/Input/Input";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useFormik } from "formik";
import toast, { Toaster } from "react-hot-toast";
import { Input } from "@material-tailwind/react";
import { ReactComponent as EyeOpen } from "../../images/eye.svg";
import { ReactComponent as EyeClosed } from "../../images/eyeClosed.svg";
import { NoSearchEngineTemplate } from "templates/NoSearchEngineTemplate";
import { PublicRoute } from "routers/PublicRoute";
import { validationSchema } from "./schema";

import Cookies from "js-cookie";
import { signIn, SignInData } from "services/sign-in";
import { handleError } from "./util";
import { LoadingSpinner } from "components/LoadingSpinner";

export interface PageLoginProps {
  className?: string;
}

const PageLogin = ({ className = "" }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({
    password: false,
    confirmNewPassword: false,
  });

  const location = useLocation();
  const companyToken = new URLSearchParams(location.search).get(
    "company_token"
  );

  const handleClickShowPassword = (field: string) => {
    setShowPassword((prevShowPassword) => ({
      ...prevShowPassword,
      [field]: !prevShowPassword[field],
    }));
  };

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      setIsLoading(true);

      let body: SignInData = {
        ...values,
      };

      if (companyToken) {
        body = {
          ...body,
          company_token: companyToken,
        };
      }

      const { accessToken, expiresIn, refreshToken, company_name } =
        await signIn(body);

      Cookies.set("auth_token", accessToken, {
        secure: window.location.protocol === "https:",
        expires: expiresIn,
        sameSite: window.location.protocol === "https:" ? "strict" : "Lax",
        path: "/",
      });

      Cookies.set("refresh_token", refreshToken, {
        secure: window.location.protocol === "https:",
        sameSite: window.location.protocol === "https:" ? "strict" : "Lax",
        path: "/",
      });

      toast.success("Bem-vindo! Redirecionando...");

      let to_home = "";

      if (company_name) {
        to_home = `/home?employee_link=${company_name}`;
      } else {
        to_home = "/home";
      }

      navigate(to_home);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === "Failed to fetch") {
          toast.error("Erro ao tentar conectar com o servidor.");
          return 0;
        }
        if (error.message === "Invalid credentials.") {
          toast.error("Senha ou e-mail inválidos. Tentar novamente.");
          return 0;
        }
        if (error.message === "User not confirmed.") {
          toast.error("E-mail ainda não confirmado,\nverifique seu e-mail para confirmá-lo.");
          return 0;
        }
        toast.error(error.message);
      } else {
        toast.error("Um erro inesperado ocorreu. Tente novamente mais tarde.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <PublicRoute>
      <NoSearchEngineTemplate>
        <div className={`${className} lg:min-h-[calc(100vh-233px)] min-h-[calc(100vh-144px)] flex flex-col justify-center items-center`} data-nc-id="PageLogin">
          <Toaster position="top-center" toastOptions={
            {
              style: {
                width: "850px",
              },
              duration: 12000,
            }
          } />
          <div className="container">
            <h2 className="flex items-center text-5xl leading-[115%] md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
              Entrar
            </h2>
            <div className="max-w-md mx-auto space-y-6">
              <form
                className="grid grid-cols-1 gap-6"
                action="#"
                method="post"
                onSubmit={formik.handleSubmit}
              >
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Endereço de e-mail
                  </span>
                  <InputTemplate
                    type="email"
                    placeholder="example@example.com"
                    className={handleError("email", formik)}
                    id="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.email && formik.touched.email && (
                    <span className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs">
                      {formik.errors.email}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                    Senha
                  </span>
                  <div className="flex flex-col items-start mb-1">
                    <Input
                      icon={
                        showPassword["password"] ? (
                          <EyeClosed
                            onClick={() => handleClickShowPassword("password")}
                            className="mt-3 cursor-pointer"
                          />
                        ) : (
                          <EyeOpen
                            onClick={() => handleClickShowPassword("password")}
                            className="mt-3 cursor-pointer"
                          />
                        )
                      }
                      id="password"
                      name="password"
                      type={showPassword["password"] ? "text" : "password"}
                      className={handleError("password", formik)}
                      placeholder="Sua senha"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.errors.password && formik.touched.password && (
                      <span className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs items-start">
                        {formik.errors.password}
                      </span>
                    )}
                  </div>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm flex justify-end mb-3"
                >
                  Esqueceu sua senha?
                </Link>

                <ButtonPrimary
                  className="bg-[--blue] hover:bg-[--blue-dark] disabled:bg-[#b2d0e0] disabled:cursor-not-allowed"
                  type="submit"
                  disabled={!formik.isValid}
                >
                  {isLoading ? <LoadingSpinner /> : "Entrar"}
                </ButtonPrimary>
              </form>

              <span className="block text-center text-neutral-700 dark:text-neutral-300">
                Ainda não tem conta? {` `}
                <Link to="/signup">Cadastre-se</Link>
              </span>
            </div>
          </div>
        </div>
      </NoSearchEngineTemplate>
    </PublicRoute>
  );
};

export default PageLogin;
